import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Bookmark as BookmarkIcon,
  CheckCircle as CheckCircleIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { AssignmentStatisticsTab } from '@/components/programs/AssignmentStatisticsTab'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { PageBreadcrumbs } from '@/components/common/PageBreadcrumbs'
import { ThemeElementList } from '@/components/programs/ThemeElementList'
import { ThemeElementDialog } from '@/components/programs/ThemeElementDialog'
import {
  ThemeCompletionDateDialog,
  type ThemeCompletionMode,
} from '@/components/programs/ThemeCompletionDateDialog'
import {
  programAssignmentsApi,
  type AssignedTheme,
  type AssignedThemeElement,
  type ProgramAssignment,
  type ThemeVisibility,
} from '@/api/programs'
import { useUserStore } from '@/stores/userStore'
import { formatDateDMY } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'
import { ROUTES } from '@/utils/constants'
import { getUserDisplayName } from '@/utils/userName'

const VISIBILITY_OPTIONS: ThemeVisibility[] = ['hidden', 'titles', 'full']

/** «Как у группы»: null-переопределение в Select нельзя выразить пустой строкой */
const INHERIT_VISIBILITY = 'inherit'

/**
 * Карточка назначенной программы глазами учителя: прогресс по темам, закладка
 * «где остановились», состав группы с персональной видимостью и управление
 * статусом назначения.
 *
 * Ученик смотрит на ту же программу через `MyProgramPage` — там нет действий и
 * будущие темы срезаны политикой видимости.
 */
export function ProgramAssignmentPage() {
  const { t } = useTranslation()
  // Два маршрута: общий /program-assignments/:id и вложенный в карточку ученика
  // /users/:id/programs/:assignmentId — там :id это ученик
  const { id, assignmentId: nestedId } = useParams<{ id: string; assignmentId?: string }>()
  const navigate = useNavigate()
  const assignmentId = Number(nestedId ?? id)
  const studentId = nestedId === undefined ? null : Number(id)

  // Имя ученика для крошек: в его разделе путь без имени не отвечает на вопрос
  // «чью программу я смотрю»
  const student = useUserStore((state) => state.currentUser)
  const fetchStudent = useUserStore((state) => state.fetchUser)

  useEffect(() => {
    if (studentId === null || Number.isNaN(studentId)) return
    fetchStudent(studentId).catch(() => {})
  }, [studentId, fetchStudent])

  const studentName =
    studentId !== null && student?.id === studentId
      ? getUserDisplayName(student, { prefer: 'full_name' })
      : null

  // Вкладка в URL, а не в состоянии: со статистикой делятся ссылкой, и после
  // F5 учитель должен остаться там же, где был
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('tab') === 'statistics' ? 'statistics' : 'themes'

  const [assignment, setAssignment] = useState<ProgramAssignment | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [expandedThemes, setExpandedThemes] = useState<number[]>([])
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuTheme, setMenuTheme] = useState<AssignedTheme | null>(null)
  const [removeTheme, setRemoveTheme] = useState<AssignedTheme | null>(null)

  // Отметка темы днём по выбору учителя: история давнего ученика переносится
  // прошлыми датами. Тема держится до конца анимации закрытия диалога.
  const [completion, setCompletion] = useState<{ theme: AssignedTheme; mode: ThemeCompletionMode } | null>(null)
  const [completionOpen, setCompletionOpen] = useState(false)

  // Правка копии: подогнать материал под конкретного ученика, не трогая
  // шаблон, который проходят остальные
  const [elementDialog, setElementDialog] = useState<
    { theme: AssignedTheme; element: AssignedThemeElement | null } | null
  >(null)
  const [deletingElement, setDeletingElement] = useState<AssignedThemeElement | null>(null)

  const [settingsOpen, setSettingsOpen] = useState(false)
  const [completeOpen, setCompleteOpen] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [draftVisibility, setDraftVisibility] = useState<ThemeVisibility>('titles')
  const [draftAutoUpdate, setDraftAutoUpdate] = useState(true)

  /**
   * silent — перечитать назначение на месте, без скелетона.
   *
   * При loading страница делает ранний return со скелетоном, то есть
   * размонтирует всё, включая открытую модалку: диалог блока успевал
   * пересоздать Editor.js посреди сохранения, а раскрытые темы схлопывались
   * после любого действия из меню.
   */
  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (options?.silent !== true) setLoading(true)
    try {
      const data = await programAssignmentsApi.get(assignmentId)
      setAssignment(data)
      setError(null)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      if (options?.silent !== true) setLoading(false)
    }
  }, [assignmentId, t])

  useEffect(() => {
    void load()
  }, [load])

  const themes = useMemo(
    () => [...(assignment?.themes ?? [])].sort((a, b) => a.position - b.position),
    [assignment],
  )
  const doneCount = themes.filter((theme) => theme.status === 'done').length
  // Даты прохождения для вкладки статистики: её эндпоинт дат не отдаёт
  const completedAtByTheme = useMemo(
    () =>
      new Map(
        themes.flatMap((theme) =>
          theme.status === 'done' && theme.completed_at ? [[theme.id, theme.completed_at] as const] : [],
        ),
      ),
    [themes],
  )

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await action()
      // Действие точечное, а перезагрузка со скелетоном стирала бы контекст:
      // раскрытые темы, позицию прокрутки и открытые диалоги
      await load({ silent: true })
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  // Тему меню сбрасывает onExited: иначе пункты закрывающегося меню
  // успевали перерисоваться под «никакую» тему
  const closeMenu = () => {
    setMenuAnchor(null)
  }

  const openCompletion = (mode: ThemeCompletionMode) => {
    const theme = menuTheme
    closeMenu()
    if (!theme) return
    setCompletion({ theme, mode })
    setCompletionOpen(true)
  }

  const openSettings = () => {
    if (!assignment) return
    setDraftName(assignment.name)
    setDraftVisibility(assignment.visibility_default)
    setDraftAutoUpdate(assignment.auto_update_enabled ?? true)
    setSettingsOpen(true)
  }

  const visibilityLabel = (value: ThemeVisibility) =>
    ({
      hidden: t('programs.visibility.hidden', 'Скрыть будущие темы'),
      titles: t('programs.visibility.titles', 'Только заголовки'),
      full: t('programs.visibility.full', 'Показывать полностью'),
    })[value]

  // Путь до страницы известен до загрузки: скелетон на крошки и заголовок
  // раздела оставлял бы пользователя без ответа на вопрос «где я»
  const breadcrumbs =
    studentId === null
      ? [{ label: t('programs.title'), to: ROUTES.PROGRAMS }]
      : [
          { label: t('users.title'), to: ROUTES.USERS },
          ...(studentName === null ? [] : [{ label: studentName, to: `${ROUTES.USERS}/${studentId}` }]),
          { label: t('programs.title'), to: ROUTES.USER_PROGRAMS(studentId) },
        ]

  if (loading || !assignment) {
    return (
      <DashboardLayout>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <PageBreadcrumbs items={breadcrumbs} />

          {!loading && <Alert severity="error">{error || t('common.error')}</Alert>}

          {loading && (
            <>
              <Skeleton variant="text" width={280} height={48} />
              <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 1, mt: 2 }} />
            </>
          )}
        </Box>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <PageBreadcrumbs items={[...breadcrumbs, { label: assignment.name }]} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <IconButton
            aria-label={t('common.back', 'Назад')}
            onClick={() => navigate(studentId === null ? ROUTES.PROGRAMS : ROUTES.USER_PROGRAMS(studentId))}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {assignment.name}
          </Typography>
          <PageHelpPopover pageKey="program-assignment" />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            size="small"
            label={
              assignment.recipient.type === 'group'
                ? t('programs.group', 'группа')
                : t('programs.student', 'ученик')
            }
          />
          {assignment.recipient.name && <Chip size="small" variant="outlined" label={assignment.recipient.name} />}
          {assignment.status === 'paused' && (
            <Chip size="small" color="warning" label={t('programs.status.paused', 'на паузе')} />
          )}
          {assignment.status === 'completed' && (
            <Chip size="small" color="success" label={t('programs.status.completed', 'завершена')} />
          )}
          {assignment.auto_update_enabled === false && (
            <Chip
              size="small"
              variant="outlined"
              label={t('programs.assignment.autoUpdateOff', 'без автообновления')}
            />
          )}
          <Typography variant="body2" color="text.secondary">
            {t('programs.my.progress', '{{done}} из {{total}} тем', { done: doneCount, total: themes.length })}
          </Typography>

          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" onClick={openSettings} disabled={busy}>
              {t('programs.assignment.settings', 'Настройки')}
            </Button>
            {assignment.status === 'active' && (
              <Button
                size="small"
                onClick={() => run(() => programAssignmentsApi.update(assignment.id, { status: 'paused' }))}
                disabled={busy}
              >
                {t('programs.assignment.pause', 'Поставить на паузу')}
              </Button>
            )}
            {assignment.status === 'paused' && (
              <Button
                size="small"
                onClick={() => run(() => programAssignmentsApi.update(assignment.id, { status: 'active' }))}
                disabled={busy}
              >
                {t('programs.assignment.resume', 'Возобновить')}
              </Button>
            )}
            {assignment.status !== 'completed' && (
              <Button
                size="small"
                variant="contained"
                onClick={() => setCompleteOpen(true)}
                disabled={busy}
              >
                {t('programs.assignment.complete', 'Завершить программу')}
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Tabs value={tab} onChange={(_, value) => setSearchParams({ tab: value })} sx={{ mb: 2 }}>
          <Tab value="themes" label={t('programs.assignment.themes')} />
          <Tab value="statistics" label={t('programs.stats.tab')} />
        </Tabs>

        {tab === 'statistics' && (
          <AssignmentStatisticsTab assignmentId={assignment.id} completedAtByTheme={completedAtByTheme} />
        )}

        {tab === 'themes' && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('programs.assignment.themes', 'Темы программы')}
            </Typography>
            <List disablePadding>
              {themes.map((theme, index) => {
                const isCurrent = assignment.bookmark.theme_id === theme.id

                const isOpen = expandedThemes.includes(theme.id)

                return (
                  <Box key={theme.id}>
                  <ListItem
                    divider={!isOpen}
                    sx={{ opacity: theme.is_archived ? 0.5 : 1 }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget)
                          setMenuTheme(theme)
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    {/* Раскрытие темы: до этого её содержимое — теорию, словарь
                        и задания — с карточки назначения посмотреть было нечем */}
                    <IconButton
                      size="small"
                      sx={{ mr: 1 }}
                      aria-label={isOpen ? t('common.collapse') : t('common.expand')}
                      onClick={() =>
                        setExpandedThemes((current) =>
                          current.includes(theme.id)
                            ? current.filter((item) => item !== theme.id)
                            : [...current, theme.id],
                        )
                      }
                    >
                      {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {/* Значки держатся рядом с названием: на телефоне
                              длинное название иначе уезжало на строку ниже,
                              оставляя галку одну */}
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            {theme.status === 'done' && <CheckCircleIcon fontSize="small" color="success" />}
                            {isCurrent && <BookmarkIcon fontSize="small" color="primary" />}
                            <Typography component="span">
                              {index + 1}. {theme.title}
                            </Typography>
                          </Box>
                          {theme.status === 'done' && theme.completed_at && (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {t('programs.completedOn', { date: formatDateDMY(theme.completed_at) })}
                            </Typography>
                          )}
                          {theme.has_source_updates && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t('programs.assignment.sourceUpdated', 'в шаблоне есть новее')}
                            />
                          )}
                          {theme.is_customized && (
                            <Chip
                              size="small"
                              variant="outlined"
                              color="warning"
                              label={t('programs.assignment.customized', 'правилось вручную')}
                            />
                          )}
                          {theme.is_archived && (
                            <Chip size="small" variant="outlined" label={t('programs.archived', 'В архиве')} />
                          )}
                        </Box>
                      }
                      secondary={theme.summary || undefined}
                    />
                  </ListItem>

                  {isOpen && (
                    <Box sx={{ pl: 7, pr: 2, pb: 2 }}>
                      {(theme.elements?.length ?? 0) > 0 ? (
                        <ThemeElementList
                          elements={theme.elements ?? []}
                          defaultExpanded
                          onEditElement={(element) => setElementDialog({ theme, element })}
                          onDeleteElement={setDeletingElement}
                          disabled={busy}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('programs.themeEmpty')}
                        </Typography>
                      )}

                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        sx={{ mt: 1 }}
                        disabled={busy}
                        onClick={() => setElementDialog({ theme, element: null })}
                      >
                        {t('programs.addElement')}
                      </Button>
                    </Box>
                  )}
                  </Box>
                )
              })}
            </List>
          </CardContent>
        </Card>
        )}

        {tab === 'themes' && assignment.recipient.type === 'group' && (assignment.students?.length ?? 0) > 0 && (
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('programs.assignment.students', 'Состав группы')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t(
                  'programs.assignment.studentsHint',
                  'Видимость будущих тем можно задать отдельно для ученика — например, открыть всю программу тому, кто занимается сам.',
                )}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {assignment.students?.map((row) => (
                <Box
                  key={row.student_id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: 1 }}
                >
                  <Typography sx={{ flex: 1, minWidth: 160 }}>
                    {row.name || `#${row.student_id}`}
                    {row.left_at && (
                      <Chip
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1 }}
                        label={t('programs.assignment.leftGroup', 'вышел из группы')}
                      />
                    )}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id={`visibility-${row.student_id}`}>
                      {t('programs.fields.visibility', 'Видимость будущих тем')}
                    </InputLabel>
                    <Select
                      labelId={`visibility-${row.student_id}`}
                      label={t('programs.fields.visibility', 'Видимость будущих тем')}
                      // Пустая строка у MUI означает «значение не выбрано» и
                      // рисует placeholder вместо подписи пункта — поэтому
                      // «как у группы» едет отдельным значением-сентинелом.
                      value={row.visibility ?? INHERIT_VISIBILITY}
                      onChange={(e) =>
                        run(() =>
                          programAssignmentsApi.setStudentVisibility(
                            assignment.id,
                            row.student_id,
                            e.target.value === INHERIT_VISIBILITY ? null : (e.target.value as ThemeVisibility),
                          ),
                        )
                      }
                      disabled={busy}
                    >
                      <MenuItem value={INHERIT_VISIBILITY}>
                        {t('programs.assignment.asGroup', 'Как у группы — {{value}}', {
                          value: visibilityLabel(assignment.visibility_default),
                        })}
                      </MenuItem>
                      {VISIBILITY_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {visibilityLabel(option)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          TransitionProps={{ onExited: () => setMenuTheme(null) }}
        >
          <MenuItem
            onClick={() => {
              const theme = menuTheme
              closeMenu()
              if (theme) void run(() => programAssignmentsApi.moveBookmark(assignment.id, { assigned_theme_id: theme.id }))
            }}
          >
            {t('programs.assignment.setBookmark', 'Поставить закладку сюда')}
          </MenuItem>
          {menuTheme?.status !== 'done' ? (
            <MenuItem onClick={() => openCompletion('complete')}>
              {t('programs.assignment.completeTheme', 'Отметить пройденной…')}
            </MenuItem>
          ) : (
            [
              <MenuItem key="change-date" onClick={() => openCompletion('change')}>
                {t('programs.assignment.changeCompletionDate')}
              </MenuItem>,
              <MenuItem
                key="reopen"
                onClick={() => {
                  const theme = menuTheme
                  closeMenu()
                  if (theme) void run(() => programAssignmentsApi.reopenTheme(assignment.id, theme.id))
                }}
              >
                {t('programs.assignment.reopenTheme', 'Вернуть в работу')}
              </MenuItem>,
            ]
          )}
          {menuTheme?.has_source_updates && (
            <MenuItem
              onClick={() => {
                const theme = menuTheme
                closeMenu()
                if (theme) void run(() => programAssignmentsApi.pullThemeFromSource(assignment.id, theme.id))
              }}
            >
              {t('programs.assignment.pullFromSource', 'Подтянуть правки из шаблона')}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setRemoveTheme(menuTheme)
              closeMenu()
            }}
          >
            {t('programs.assignment.removeTheme', 'Убрать из программы')}
          </MenuItem>
        </Menu>

        <ThemeCompletionDateDialog
          open={completionOpen}
          theme={completion?.theme ?? null}
          mode={completion?.mode ?? 'complete'}
          onClose={() => setCompletionOpen(false)}
          onExited={() => setCompletion(null)}
          onSubmit={async (completedAt) => {
            const target = completion
            if (!target) return

            await programAssignmentsApi.completeTheme(assignment.id, target.theme.id, completedAt)
            setCompletionOpen(false)
            setError(null)
            await load({ silent: true })
          }}
        />

        <Dialog open={Boolean(removeTheme)} onClose={() => setRemoveTheme(null)} fullWidth maxWidth="xs">
          <DialogTitle>{t('programs.assignment.removeTheme', 'Убрать из программы')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(
                'programs.assignment.removeThemeConfirm',
                'Тема «{{title}}» исчезнет из программы ученика. История прохождения сохранится.',
                { title: removeTheme?.title ?? '' },
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoveTheme(null)}>{t('common.cancel', 'Отмена')}</Button>
            <Button
              color="error"
              variant="contained"
              disabled={busy}
              onClick={() => {
                const theme = removeTheme
                setRemoveTheme(null)
                if (theme) void run(() => programAssignmentsApi.archiveTheme(assignment.id, theme.id))
              }}
            >
              {t('programs.assignment.removeThemeAction', 'Убрать')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={completeOpen} onClose={() => setCompleteOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>{t('programs.assignment.complete', 'Завершить программу')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(
                'programs.assignment.completeConfirm',
                'Программа станет завершённой и освободит место для новой. Пройденное останется ученику доступным.',
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCompleteOpen(false)}>{t('common.cancel', 'Отмена')}</Button>
            <Button
              variant="contained"
              disabled={busy}
              onClick={() => {
                setCompleteOpen(false)
                void run(() => programAssignmentsApi.update(assignment.id, { status: 'completed' }))
              }}
            >
              {t('programs.assignment.complete', 'Завершить программу')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t('programs.assignment.settings', 'Настройки')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="dense"
              label={t('programs.fields.name', 'Название')}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel id="assignment-visibility">
                {t('programs.fields.visibility', 'Видимость будущих тем')}
              </InputLabel>
              <Select
                labelId="assignment-visibility"
                label={t('programs.fields.visibility', 'Видимость будущих тем')}
                value={draftVisibility}
                onChange={(e) => setDraftVisibility(e.target.value as ThemeVisibility)}
              >
                {VISIBILITY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {visibilityLabel(option)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={draftAutoUpdate}
                  onChange={(e) => setDraftAutoUpdate(e.target.checked)}
                />
              }
              label={t('programs.assignment.autoUpdate', 'Обновлять из программы автоматически')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
              {t('programs.assignment.autoUpdateHint')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSettingsOpen(false)}>{t('common.cancel', 'Отмена')}</Button>
            <Button
              variant="contained"
              disabled={!draftName.trim() || busy}
              onClick={() => {
                setSettingsOpen(false)
                void run(() =>
                  programAssignmentsApi.update(assignment.id, {
                    name: draftName.trim(),
                    visibility_default: draftVisibility,
                    auto_update_enabled: draftAutoUpdate,
                  }),
                )
              }}
            >
              {t('common.save', 'Сохранить')}
            </Button>
          </DialogActions>
        </Dialog>

        <ThemeElementDialog
          open={elementDialog !== null}
          themeTitle={elementDialog?.theme.title ?? ''}
          element={elementDialog?.element ?? null}
          onClose={() => setElementDialog(null)}
          onSave={async (payload) => {
            const target = elementDialog
            if (!target) return

            await (target.element
              ? programAssignmentsApi.updateAssignedElement(target.element.id, {
                  title: payload.title,
                  body_json: payload.body_json,
                  word_ids: payload.word_ids,
                })
              : programAssignmentsApi.createAssignedElement(target.theme.id, payload))

            // Закрываем до перезагрузки: иначе диалог остаётся смонтированным
            // на время запроса и Editor.js успевает инициализироваться заново
            setElementDialog(null)
            await load({ silent: true })
          }}
        />

        <Dialog open={deletingElement !== null} onClose={() => setDeletingElement(null)}>
          <DialogTitle>{t('programs.deleteElement')}</DialogTitle>
          <DialogContent>
            <Typography>{t('programs.deleteAssignedElementConfirm')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeletingElement(null)}>{t('common.cancel')}</Button>
            <Button
              color="error"
              variant="contained"
              disabled={busy}
              onClick={() => {
                const target = deletingElement
                setDeletingElement(null)
                if (target) void run(() => programAssignmentsApi.deleteAssignedElement(target.id))
              }}
            >
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
